import React, { useState, useMemo } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AdminSurveyQuestionListItemDto, QuestionType } from '@wellverse/shared-ts';

export const SurveyQuestionList: React.FC = () => {
  const { tableProps } = useTable<AdminSurveyQuestionListItemDto>({
    resource: 'survey-questions',
    pagination: { current: 1, pageSize: 20 },
  });

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.questionKey?.toLowerCase().includes(lower) ||
      item.questionText?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  return (
    <List resource="survey-questions">
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by question key or text..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column dataIndex="questionKey" title="Question Key" />
        <Table.Column dataIndex="questionText" title="Question Text" />
        <Table.Column
          dataIndex="type"
          title="Type"
          filters={Object.values(QuestionType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: any) => record.type === value}
        />
        <Table.Column
          dataIndex="isRequired"
          title="Required"
          filters={[
            { text: 'Required', value: true },
            { text: 'Optional', value: false },
          ]}
          onFilter={(value, record: any) => record.isRequired === value}
          render={(isRequired: boolean) => (
            <Tag
              style={{
                borderColor: isRequired ? '#3ecf8e' : '#898989',
                color: isRequired ? '#3ecf8e' : '#898989',
                background: isRequired ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isRequired ? 'Required' : 'Optional'}
            </Tag>
          )}
        />
        <Table.Column dataIndex="orderIndex" title="Order" />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag
              style={{
                borderColor: isActive ? '#3ecf8e' : '#898989',
                color: isActive ? '#3ecf8e' : '#898989',
                background: isActive ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column dataIndex="optionCount" title="Options" />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: AdminSurveyQuestionListItemDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
